/**
 * LibraryHealthDashboard
 *
 * Curation snapshot for the client persona library. Surfaces three signals
 * that admins typically want to act on:
 *
 *   - Orphans         — personas not referenced by any scenario and
 *                       unmodified for >90 days. Candidates for archival.
 *   - Near-duplicates — pairs with high embedding similarity and no lineage
 *                       relationship. Likely unmerged forks.
 *   - Missing fields  — counts of personas missing image, tags, category,
 *                       presenting concerns, embeddings, or cultural context.
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button, Chip, Alert,
  CircularProgress, Stack, Divider, IconButton, Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HealingIcon from '@mui/icons-material/Healing';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getLibraryHealth } from '../../../services/personaLibraryApi';

const StatCard = ({ label, value, color = 'text.primary', hint }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="h4" sx={{ color }}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </CardContent>
  </Card>
);

const PersonaLink = ({ persona }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Typography variant="body2" component="span">{persona.name}</Typography>
    <Typography variant="caption" color="text.secondary">({persona.key})</Typography>
    <Tooltip title="Open in library">
      <IconButton
        size="small"
        href={`/admin/dashboard/persona-library?focus=${encodeURIComponent(persona.key)}`}
        target="_blank"
      >
        <OpenInNewIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  </Box>
);

const LibraryHealthDashboard = ({ token }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [threshold, setThreshold] = useState(0.95);

  const load = async (opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getLibraryHealth({ threshold: opts.threshold || threshold }, token);
      setReport(data);
    } catch (e) {
      setError(e.msg || e.message || 'Failed to load library health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && !report) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  if (!report) return null;

  const { totals, orphans, nearDuplicates, missingFields, missingFieldExamples, meta } = report;

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5">Persona Library Health</Typography>
          <Typography variant="body2" color="text.secondary">
            Curation signal for the client persona library. Threshold: {threshold} similarity; orphan grace period: {meta.orphanGracePeriodDays} days.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={() => load()}>Refresh</Button>
      </Box>

      {/* Summary stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Total personas" value={totals.personas} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Embedded"
            value={totals.embedded}
            color={totals.embedded < totals.personas ? 'warning.main' : 'success.main'}
            hint={`${totals.personas - totals.embedded} pending`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Orphans" value={totals.orphans} color={totals.orphans > 0 ? 'warning.main' : 'text.primary'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Near-duplicates" value={totals.nearDuplicates} color={totals.nearDuplicates > 0 ? 'warning.main' : 'text.primary'} />
        </Grid>
      </Grid>

      {/* Near-duplicates section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <HealingIcon color="warning" />
          <Typography variant="h6">Near-duplicate personas</Typography>
          {meta.truncated && (
            <Chip size="small" label="capped at 1000 personas" color="warning" variant="outlined" />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Pairs with cosine similarity ≥ {threshold} that are not in a lineage relationship. Likely unmerged forks — review and consolidate where appropriate.
        </Typography>
        {nearDuplicates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No near-duplicates above the threshold. 🎯</Typography>
        ) : (
          <Stack spacing={1}>
            {nearDuplicates.map((pair, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <PersonaLink persona={pair.a} />
                    <PersonaLink persona={pair.b} />
                  </Box>
                  <Chip
                    label={`${(pair.similarity * 100).toFixed(1)}%`}
                    color="warning"
                    size="small"
                  />
                </Box>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Orphans section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <WarningAmberIcon color="warning" />
          <Typography variant="h6">Orphan personas</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Not referenced by any training scenario and unmodified for {meta.orphanGracePeriodDays}+ days. Candidates for archival or merging into an active variant.
        </Typography>
        {orphans.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No orphan personas. 🎯</Typography>
        ) : (
          <Stack spacing={0.5}>
            {orphans.slice(0, 30).map(p => (
              <Box key={p._id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <PersonaLink persona={p} />
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip size="small" label={p.status || 'unknown'} variant="outlined" />
                  <Typography variant="caption" color="text.secondary">
                    last edit {new Date(p.updatedAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            ))}
            {orphans.length > 30 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                + {orphans.length - 30} more not shown
              </Typography>
            )}
          </Stack>
        )}
      </Paper>

      {/* Missing fields section */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Missing fields</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Personas with key metadata gaps — opportunities to enrich the library.
        </Typography>
        <Grid container spacing={2}>
          <MissingFieldBlock label="No persona image" count={missingFields.noImage} examples={missingFieldExamples.noImage} />
          <MissingFieldBlock label="No presenting concerns" count={missingFields.noPresentingConcerns} />
          <MissingFieldBlock label="No tags" count={missingFields.noTags} examples={missingFieldExamples.noTags} />
          <MissingFieldBlock label="No category" count={missingFields.noCategory} />
          <MissingFieldBlock label="No embedding" count={missingFields.noEmbedding} examples={missingFieldExamples.noEmbedding} />
          <MissingFieldBlock label="No cultural context" count={missingFields.noCulturalContext} />
        </Grid>
      </Paper>
    </Box>
  );
};

const MissingFieldBlock = ({ label, count, examples }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="h6" color={count > 0 ? 'warning.main' : 'text.primary'}>{count}</Typography>
      {examples?.length > 0 && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Stack spacing={0.25}>
            {examples.map(e => (
              <PersonaLink key={e._id} persona={e} />
            ))}
          </Stack>
        </>
      )}
    </Paper>
  </Grid>
);

export default LibraryHealthDashboard;
